'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import type { UIMessage } from 'ai';
import { AI_SEARCH_EVENTS, type AiSearchAnalytics } from './ai-search-analytics';
import { dispatchAssistantTurnAnalytics, type AssistantTurnStatus } from './assistant-turn-analytics';
import { classifyRefusalFromError } from './refusal-classifier';

/**
 * Fires `results_shown` / `no_results` exactly once per assistant turn, on the
 * transition into `'ready'`. Lives in a hook (the guide's accepted escape
 * hatch) so the caller doesn't carry the status-diff ref + dedupe Set inline.
 * The dedupe Set is keyed on assistant message id; the previous-status ref
 * detects the transition.
 */
export function useTrackAssistantTurns(input: {
  status: AssistantTurnStatus;
  messages: ReadonlyArray<UIMessage>;
  sessionId: string | null;
  analytics: AiSearchAnalytics;
  consumeSubmitContext: (threadId: string) => {
    latencyMs: number;
    modelExtraction: string;
    promptLength: number;
  };
  onDispatched: () => void;
}): void {
  const previousStatusRef = useRef<AssistantTurnStatus>(input.status);
  const trackedAssistantIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const previousStatus = previousStatusRef.current;
    previousStatusRef.current = input.status;
    const dispatched = dispatchAssistantTurnAnalytics({
      status: input.status,
      previousStatus,
      messages: input.messages,
      sessionId: input.sessionId,
      trackedAssistantIds: trackedAssistantIdsRef.current,
      consumeSubmitContext: input.consumeSubmitContext,
      analytics: input.analytics
    });
    if (dispatched) input.onDispatched();
    // The dispatcher reads everything it needs from the input snapshot. The
    // hook owns the dedupe refs; consumers don't need to manage them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.status, input.messages, input.sessionId]);
}

/**
 * Captures the `REFUSAL` analytics event whenever `useChat.error` flips to a
 * NEW error reference. Same escape-hatch encapsulation as
 * `useTrackAssistantTurns` — the dedupe ref lives in the hook, not in the
 * calling component.
 */
export function useTrackChatErrors(input) {
  const $ = _c(5);
  const trackedErrorRef = useRef(null);
  let t0;
  let t1;
  if ($[0] !== input.analytics || $[1] !== input.error || $[2] !== input.sessionId) {
    t0 = () => {
      if (!input.error) {
        return;
      }
      if (trackedErrorRef.current === input.error) {
        return;
      }
      trackedErrorRef.current = input.error;
      if (!input.sessionId) {
        return;
      }
      input.analytics[AI_SEARCH_EVENTS.REFUSAL]({
        block_reason: classifyRefusalFromError(input.error),
        session_id: input.sessionId
      });
    };
    t1 = [input.error, input.sessionId, input.analytics];
    $[0] = input.analytics;
    $[1] = input.error;
    $[2] = input.sessionId;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
}